import React, { useState, useEffect, useContext, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useParams, useNavigate } from "react-router-dom";
import { UserContext } from '../../context/UserContext';
import CheckoutForm from "../CheckoutForm/CheckoutForm";
import "../../App.css";
import './BaseCheckout.css';

const stripePromise = loadStripe(process.env.REACT_APP_VLA_STRIPE_PUBLISHABLE_KEY);

const BaseCheckout = () => {
    const { sessionToken } = useParams();
    const { user, setUser } = useContext(UserContext);
    const [clientSecret, setClientSecret] = useState("");
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [sessionTokenData, setSessionTokenData] = useState(null);
    const [userChecked, setUserChecked] = useState(false);
    const [error, setError] = useState(null);
    const hasFetchedSessionData = useRef(false);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!user && storedUser) {
            const parsedUser = JSON.parse(storedUser);
            if (new Date().getTime() > parsedUser.expiry) {
                localStorage.removeItem('user');
                setUser(null);
                alert('Session expired. Please log in again.');
                navigate('/login', { state: { redirectTo: `/checkout/${sessionToken}` } });
            } else {
                setUser(parsedUser);
            }
        } else if (!user) {
            navigate('/login', { state: { redirectTo: `/checkout/${sessionToken}` } });
        }
        setUserChecked(true);
    }, [user, setUser, navigate]);

    useEffect(() => {
        if (!hasFetchedSessionData.current && !sessionTokenData && user) {
            hasFetchedSessionData.current = true;
            const fetchSessionData = async () => {
                setLoading(true);
                try {
                    const requestBody = {
                        email: user.email
                    };

                    const response = await fetch('https://3nrqnhje08.execute-api.us-east-2.amazonaws.com/api/Checkout/GetCheckoutFieldsAndGetSearchSessionToken', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(requestBody),
                    });

                    if (!response.ok) {
                        throw new Error('Failed to fetch session details');
                    }

                    const data = await response.json();
                    if (data) {

                        data.cartItems = [];

                        var numCartItemsSave = data.numberOfCartItems;
                        var paymentSessionTokenSave = data.paymentSessionToken;

                        // batch get cart items
                        var numberOfCartDownloads = Math.ceil(numCartItemsSave / 10); // 10 cart items each time

                        for (let downloadCounter = 0; downloadCounter < numberOfCartDownloads; downloadCounter++) {

                            const startItem = downloadCounter * 10;
                            const endItem = Math.min(startItem + 9, numCartItemsSave - 1);

                            const cartItemRequestBody = {
                                paymentSessionToken: paymentSessionTokenSave,
                                startCartItemNumberInclusive: startItem,
                                endCartItemNumberInclusive: endItem
                            };

                            const cartItemsResponse = await fetch('https://3nrqnhje08.execute-api.us-east-2.amazonaws.com/api/Checkout/GetCheckoutFieldsAndGetSearchSessionToken_CartItems', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify(cartItemRequestBody),
                            });

                            if (!cartItemsResponse.ok) {
                                throw new Error('Failed to fetch session details');
                            }

                            const dataCartItems = await cartItemsResponse.json();

                            dataCartItems.forEach((item, index) => {
                                data.cartItems.push(item);
                            });
                        }

                        setSessionTokenData(data);
                    } else {
                        setError('No session data found or access denied.');
                    }
                } catch (err) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchSessionData();
        }
    }, [userChecked]);

    useEffect(() => {
        if (!sessionTokenData) {
            return;
        }

        var cartItemsFromSessionToken = [];

        for (let i = 0; i < sessionTokenData.cartItems.length; i++) {
            const item = sessionTokenData.cartItems[i];

            cartItemsFromSessionToken.push({
                id: item.driverName + "_" + (i + 1),
                amount: item.price * 100
            });
        }

        cartItemsFromSessionToken.push({
            id: "shipping",
            amount: (sessionTokenData.shippingAmount * 100)
        });

        fetch("https://3nrqnhje08.execute-api.us-east-2.amazonaws.com/api/Stripe/create-payment-intent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                items: cartItemsFromSessionToken
            })
        })
            .then((res) => res.json())
            .then((data) => setClientSecret(data.clientSecret));
    }, [sessionTokenData]);

    const appearance = {
        theme: 'stripe',
    };
    // Enable the skeleton loader UI for optimal loading.
    const loader = 'auto';

    if (!userChecked || loading || (!sessionTokenData && !error)) {
        return <div className="loading-screen">Loading checkout details...</div>;
    }

    if (sessionTokenData != null && sessionTokenData.paymentSessionToken === sessionToken && sessionTokenData.isFullyPaid) {
        return (<div className="order-confirmation-container">
            <h2>No Payment Needed At This Time</h2>
            <div className="order-details">
                <p><strong>Total Already Paid:</strong> ${sessionTokenData.cartTotalAmount.toFixed(2)}</p>
            </div>
        </div>)
    }

    return (
        <div className="base-checkout-container">
            <div className="base-checkout-left-side">
                <div className="base-checkout-cart-section">
                    <h2 className="base-checkout-h2">Cart Items</h2>
                    <div className="base-checkout-cart-items">
                        {sessionTokenData.numberOfCartItems > 0 ? (
                            sessionTokenData.cartItems.map((item, index) => (
                                <div key={index} className="base-checkout-cart-item">
                                    <img
                                        src={`data:image/jpeg;base64,${item.imageBase64}`}
                                        alt={item.driverName}
                                        className="base-checkout-cart-item-image"
                                    />
                                    <div className="base-checkout-cart-item-details">
                                        <span className="base-checkout-cart-item-driver">
                                            Driver: {item.driverName}
                                        </span>
                                        <span className="base-checkout-cart-item-price">
                                            ${item.price.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p>Your cart is empty</p>
                        )}
                    </div>
                    <div className="base-checkout-cart-total">
                        <span>Shipping:</span>
                        <span>
                            ${sessionTokenData.shippingAmount.toFixed(2)}
                        </span>
                    </div>
                    <div className="base-checkout-cart-total">
                        <span>Total:</span>
                        <span>
                            ${(sessionTokenData.cartItems
                                .reduce((acc, item) => acc + item.price, 0) + sessionTokenData.shippingAmount)
                                .toFixed(2)}
                        </span>
                    </div>
                </div>
            </div>
            <div className="base-checkout-right-side">
                {clientSecret && (
                    <Elements
                        options={{ clientSecret, appearance, loader }}
                        stripe={stripePromise}
                    >
                        <CheckoutForm sessionToken={sessionToken} payerEmail={user.email} />
                    </Elements>
                )}
            </div>
        </div>
    );
}

export default BaseCheckout;